import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../api";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Alert,
} from "@mui/material";
import {
  Devices,
  CheckCircle,
  PendingActions,
  Logout,
  AccountCircle,
  QrCode2,
  Download,
  Print,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AcknowledgeDialog from "./AcknowledgeDialog";
import ForcePasswordChangeDialog from "../common/ForcePasswordChangeDialog";
import { logoutSession } from "../../utils/session";

import { ASSET_CATEGORIES as CATEGORY_ORDER } from "../../constants/assetCategories";

function MyDevices() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("adminUser") || "null");

  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [qrBusyId, setQrBusyId] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [forcePw, setForcePw] = useState(
    () => localStorage.getItem("requirePasswordChange") === "true"
  );

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/assets/mine");
      setDevices(res.data.result || []);
    } catch (err) {
      toast.error("Failed to load your devices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) {
      navigate("/login");
      return;
    }
    axios
      .get("/settings")
      .then((res) => setSettings(res.data.result))
      .catch(() => {});
    fetchDevices();
  }, [fetchDevices, navigate]);

  const handleLogout = async () => {
    setMenuAnchor(null);
    await logoutSession(axios);
    navigate("/login");
  };

  const openAck = (device) => {
    setSelected(device);
    setAckOpen(true);
  };

  const qrPayload = (d) =>
    JSON.stringify({
      id: d._id,
      tag: d.assetTag || null,
      serial: d.serialNumber || null,
      url: `${window.location.origin}/my-devices`,
    });

  const fetchLabelBlob = async (assetId, format = "pdf") => {
    const r = await axios.get(`/assets/${assetId}/label?format=${format}`, {
      responseType: "blob",
    });
    return r.data;
  };

  const downloadQr = async (d) => {
    try {
      setQrBusyId(d._id);
      const blob = await fetchLabelBlob(d._id, "pdf");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asset-${d.assetTag || d.serialNumber || d._id}-label.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Asset label downloaded — print and affix to the device.");
    } catch {
      toast.error("Could not download asset label.");
    } finally {
      setQrBusyId(null);
    }
  };

  const printQr = async (d) => {
    try {
      setQrBusyId(d._id);
      const blob = await fetchLabelBlob(d._id, "pdf");
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) {
        URL.revokeObjectURL(url);
        toast.error("Allow pop-ups to print the asset label.");
        return;
      }
      // Give the PDF viewer a moment, then print
      setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch {
          /* viewer may handle print UI */
        }
      }, 700);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Could not open asset label for print.");
    } finally {
      setQrBusyId(null);
    }
  };

  const appName = settings?.app?.appName || "Asset Undertaking System";
  const pending = devices.filter((d) => !d.acknowledged);
  const needsVerify = devices.filter(
    (d) => d.acknowledged && d.needsVerification
  );
  const acknowledged = devices.filter(
    (d) => d.acknowledged && !d.needsVerification
  );

  const confirmVerify = async (d) => {
    if (
      !window.confirm(
        `Confirm you still have "${d.model || d.assetTag || d.serialNumber || "this device"}"?`
      )
    ) {
      return;
    }
    setVerifyingId(d._id);
    try {
      const res = await axios.post("/undertaking/confirm-verify", {
        assetId: d._id,
      });
      toast.success(res.data?.message || "Device verified");
      setDevices((prev) =>
        prev.map((x) =>
          x._id === d._id
            ? {
                ...x,
                needsVerification: false,
                verificationStatus: "Verified",
              }
            : x
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not verify device");
    } finally {
      setVerifyingId(null);
    }
  };

  const categoriesPresent = useMemo(() => {
    const set = new Set(devices.map((d) => d.category || "Other"));
    return CATEGORY_ORDER.filter((c) => set.has(c)).concat(
      [...set].filter((c) => !CATEGORY_ORDER.includes(c)).sort()
    );
  }, [devices]);

  const StackChips = ({ d }) => (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
      {d.category && (
        <Chip size="small" variant="outlined" label={d.category} color="primary" />
      )}
      <Chip
        size="small"
        icon={
          d.needsVerification ? (
            <PendingActions />
          ) : d.acknowledged ? (
            <CheckCircle />
          ) : (
            <PendingActions />
          )
        }
        label={
          d.needsVerification
            ? "Verification needed"
            : d.acknowledged
              ? "Acknowledged"
              : "Action needed"
        }
        color={
          d.needsVerification ? "warning" : d.acknowledged ? "success" : "warning"
        }
      />
    </Box>
  );

  const DeviceCard = ({ d }) => (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderColor: d.acknowledged ? "divider" : "warning.light",
        boxShadow: d.acknowledged ? "none" : "0 0 0 1px rgba(237,108,2,0.18)",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <Devices color="primary" sx={{ mt: 0.35 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {d.model || d.assetTag || d.category || "Device"}
            </Typography>
            <StackChips d={d} />
          </Box>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(96px, 38%) 1fr",
            columnGap: 1.5,
            rowGap: 0.75,
          }}
        >
          {[
            ["Category", d.category],
            ["Asset Tag", d.assetTag],
            ["Serial", d.serialNumber],
            ["Model No", d.modelNo],
            ["Manufacturer", d.manufacturer],
            ["Specifications", d.specifications],
            ["Condition", d.condition],
            ["Status", d.status],
            ["Location", d.location],
            [
              "Assigned",
              d.assignedAt ? new Date(d.assignedAt).toLocaleDateString() : null,
            ],
            [
              "Warranty",
              d.warrantyExpiry
                ? new Date(d.warrantyExpiry).toLocaleDateString()
                : d.warrantyStatus && d.warrantyStatus !== "Unknown"
                  ? d.warrantyStatus
                  : null,
            ],
          ]
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <React.Fragment key={label}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
                  {value}
                </Typography>
              </React.Fragment>
            ))}
        </Box>
        {d.acknowledged && d.acknowledgedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            Acknowledged on {new Date(d.acknowledgedAt).toLocaleDateString()}
          </Typography>
        )}

        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
            bgcolor: "grey.50",
          }}
        >
          <Box
            sx={{
              p: 0.75,
              bgcolor: "#fff",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              lineHeight: 0,
            }}
          >
            <QRCodeSVG value={qrPayload(d)} size={88} level="M" includeMargin={false} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 140 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <QrCode2 fontSize="small" /> Asset label
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Print the company asset sticker and affix it to this device.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Print />}
                disabled={qrBusyId === d._id}
                onClick={() => printQr(d)}
              >
                Print label
              </Button>
              <Button
                size="small"
                startIcon={<Download />}
                disabled={qrBusyId === d._id}
                onClick={() => downloadQr(d)}
              >
                PDF
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0, flexDirection: "column", gap: 1, alignItems: "stretch" }}>
        {d.needsVerification ? (
          <Button
            size="small"
            variant="contained"
            color="warning"
            fullWidth
            disabled={verifyingId === d._id}
            onClick={() => confirmVerify(d)}
          >
            {verifyingId === d._id
              ? "Confirming…"
              : "Confirm I still have this device"}
          </Button>
        ) : d.acknowledged ? (
          <Button size="small" disabled fullWidth>
            Completed
          </Button>
        ) : (
          <Button size="small" variant="contained" fullWidth onClick={() => openAck(d)}>
            Review &amp; Acknowledge
          </Button>
        )}
      </CardActions>
    </Card>
  );

  const renderSection = (title, list) => {
    if (!list.length) return null;
    const byCategory = CATEGORY_ORDER.concat(
      [...new Set(list.map((d) => d.category || "Other"))].filter(
        (c) => !CATEGORY_ORDER.includes(c)
      )
    )
      .map((cat) => ({
        cat,
        items: list.filter((d) => (d.category || "Other") === cat),
      }))
      .filter((g) => g.items.length > 0);

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          {title} ({list.length})
        </Typography>
        {byCategory.map(({ cat, items }) => (
          <Box key={`${title}-${cat}`} sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {cat}
              </Typography>
              <Chip size="small" label={items.length} />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {items.map((d) => (
                <DeviceCard key={d._id} d={d} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)" }}
      >
        <Toolbar>
          <Devices sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {appName}
          </Typography>
          <IconButton
            color="inherit"
            aria-label="Account menu"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user?.name || "Employee"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout fontSize="small" sx={{ mr: 1 }} />
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            {(user?.name || "E").charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Welcome{user?.name ? `, ${user.name}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review each assigned item, acknowledge receipt, and print a QR label to attach.
            </Typography>
          </Box>
        </Box>

        {!loading && pending.length > 0 && (
          <Alert
            severity="warning"
            icon={<PendingActions />}
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => openAck(pending[0])}
              >
                Start
              </Button>
            }
          >
            You have <strong>{pending.length}</strong> device
            {pending.length === 1 ? "" : "s"} waiting for acknowledgment.
          </Alert>
        )}

        {!loading && needsVerify.length > 0 && (
          <Alert severity="warning" icon={<PendingActions />} sx={{ mt: 2 }}>
            You have <strong>{needsVerify.length}</strong> device
            {needsVerify.length === 1 ? "" : "s"} awaiting verification confirmation
            (IT campaign). Use <strong>Confirm I still have this device</strong> on
            each card.
          </Alert>
        )}

        {!loading && devices.length > 0 && (
          <Alert severity="info" icon={<QrCode2 />} sx={{ mt: 2 }}>
            <strong>Affix an asset label:</strong> on each device card, use{" "}
            <strong>Print label</strong> or <strong>PDF</strong>, then stick it on the
            physical device.
          </Alert>
        )}

        {!loading && devices.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 2 }}>
            <Chip
              size="small"
              color={pending.length ? "warning" : "success"}
              label={
                pending.length
                  ? `${pending.length} pending`
                  : "All acknowledged"
              }
            />
            {categoriesPresent.map((c) => (
              <Chip
                key={c}
                size="small"
                label={`${c}: ${devices.filter((d) => (d.category || "Other") === c).length}`}
                variant="outlined"
              />
            ))}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : devices.length === 0 ? (
          <Card variant="outlined" sx={{ p: 5, textAlign: "center", mt: 4 }}>
            <Devices sx={{ fontSize: 56, color: "text.disabled", mb: 1 }} />
            <Typography variant="h6">No devices assigned</Typography>
            <Typography variant="body2" color="text.secondary">
              You don&apos;t have any devices assigned to you right now. If you believe this
              is wrong, contact IT support.
            </Typography>
          </Card>
        ) : (
          <>
            {renderSection("Pending acknowledgment", pending)}
            {renderSection("Verification needed", needsVerify)}
            {renderSection("Acknowledged", acknowledged)}
          </>
        )}
      </Container>

      <AcknowledgeDialog
        open={ackOpen}
        asset={selected}
        settings={settings}
        onClose={() => setAckOpen(false)}
        onSubmitted={fetchDevices}
      />

      <ForcePasswordChangeDialog
        open={forcePw}
        userId={user?.id || user?._id}
        onComplete={() => setForcePw(false)}
      />

      <ToastContainer position="top-center" autoClose={2800} />
    </Box>
  );
}

export default MyDevices;
